import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Lock, ShieldCheck, AlertCircle, Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { useTenant } from "../../context/TenantContext";
import { TenantLogo } from "../../components/TenantBranding";
import { getApiUrl } from "../../config/api";
import "../../styles/ResetPassword.css";

const validatePassword = (password: string): string | null => {
  if (!password || password.length < 8) return "Master key must be at least 8 characters long.";
  if (!/(?=.*[a-z])/.test(password)) return "Identity fragment missing: Lowercase character required.";
  if (!/(?=.*[A-Z])/.test(password)) return "Identity fragment missing: Uppercase character required.";
  if (!/(?=.*\d)/.test(password)) return "Identity fragment missing: Numerical value required.";
  return null;
};

const ResetPassword: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { branding } = useTenant();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"error" | "success">("error");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    document.title = `Reset Password | ${branding.displayName}`;
  }, [branding.displayName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage("");
    setMessageType("error");

    if (!token) {
      setMessage("Security anchor missing: Invalid reset dispatch.");
      return;
    }

    const validationError = validatePassword(password);
    if (validationError) {
      setMessage(validationError);
      return;
    }

    if (password !== confirm) {
      setMessage("Cryptographic mismatch: Passwords do not align.");
      return;
    }

    setIsLoading(true);
    try {
      await axios.post(getApiUrl(`auth/reset-password/${token}`), { password });
      setMessageType("success");
      setMessage("Credential reconstruction complete. Redirecting to access terminal...");
      setTimeout(() => navigate("/signin"), 2500);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Protocol error: Link may have expired or timed out.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full -ml-80 -mt-80 blur-3xl animate-pulse brand-soft-bg"></div>
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full -mr-80 -mb-80 blur-3xl animate-pulse"
        style={{ animationDelay: "1s", backgroundColor: branding.secondarySoft }}
      ></div>

      <div className="flex-grow flex items-center justify-center p-6 relative z-10">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl shadow-slate-900/5 border border-slate-100 overflow-hidden">
          <div className="h-2 brand-surface-accent" />

          <div className="p-10">
            <div className="flex justify-center mb-10">
              <TenantLogo size="lg" showText />
            </div>

            <div className="text-center mb-10">
              <h2 className="text-3xl font-black text-slate-800 tracking-tight mb-3">
                Credential Reconstruction
              </h2>
              <p className="text-sm font-medium text-slate-400 max-w-xs mx-auto">
                Configure your new master identity key to regain access to your academic terminal.
              </p>
            </div>

            {message && (
              <div
                className={`mb-8 p-4 rounded-2xl flex items-start gap-3 animate-in zoom-in-95 duration-300 ${
                  messageType === "success"
                    ? "bg-teal-50 text-teal-700 border border-teal-100"
                    : "bg-red-50 text-red-700 border border-red-100"
                }`}
              >
                {messageType === "success" ? (
                  <ShieldCheck className="shrink-0 mt-0.5" size={18} />
                ) : (
                  <AlertCircle className="shrink-0 mt-0.5" size={18} />
                )}
                <p className="text-xs font-bold leading-relaxed">{message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  New Secure Key
                </label>
                <div className="relative">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300">
                    <Lock size={18} />
                  </div>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Enter your new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full pl-14 pr-12 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white transition-all brand-input-focus"
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 transition-colors"
                    style={{ color: showPass ? branding.primaryColor : undefined }}
                  >
                    {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Confirm Identity Key
                </label>
                <div className="relative">
                  <div className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300">
                    <ShieldCheck size={18} />
                  </div>
                  <input
                    type={showPass ? "text" : "password"}
                    placeholder="Confirm your new password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="w-full pl-14 pr-6 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-xs font-bold text-slate-700 outline-none focus:bg-white transition-all brand-input-focus"
                    disabled={isLoading || messageType === "success"}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || messageType === "success"}
                className="w-full brand-button-primary font-black py-4 rounded-2xl shadow-xl transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-3 text-[10px] uppercase tracking-[0.2em] mt-2"
              >
                {isLoading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    <span>Confirm Override</span>
                    <ArrowRight size={14} />
                  </>
                )}
              </button>
            </form>

            <div className="mt-8 text-center transition-all">
              <Link
                to="/signin"
                className="text-[10px] font-black uppercase tracking-widest text-slate-400 transition-colors brand-link"
              >
                Return to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
